/**
 * HL-11800 — campaign attribution for the email-step lead.
 *
 * The funnel's tracking bootstrap (`window.eventsTracking`) keeps one `arrivals` entry per
 * landing on the site, each with the full landing URL (MixPanelTracker reads the same list
 * for its last-touch UTMs). Nothing is persisted server-side, so the lead POST carries the
 * campaign parameters of the **last** arrival — the visit the email was given in — and they
 * are stored on the `leads` row as JSON and forwarded to HubSpot.
 */

export const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "gclid",
  "fbclid",
] as const;

export type UtmKey = (typeof UTM_KEYS)[number];
export type UtmParams = Partial<Record<UtmKey, string>>;

const MAX_VALUE_LENGTH = 255;

/** Whitelisted campaign parameters of one landing URL. Accepts the "?x=y" short form too. */
export const extractUtms = (rawUrl: string | null | undefined): UtmParams => {
  const out: UtmParams = {};
  if (!rawUrl) return out;
  let params: URLSearchParams;
  try {
    // iOS may store only the query string when localStorage is cross-subdomain blocked.
    const url = rawUrl.startsWith("?") ? new URL("http://a.com" + rawUrl) : new URL(rawUrl);
    params = url.searchParams;
  } catch {
    return out;
  }
  for (const key of UTM_KEYS) {
    const value = params.get(key);
    if (value && value.trim()) out[key] = value.trim().slice(0, MAX_VALUE_LENGTH);
  }
  return out;
};

/**
 * Campaign parameters of the visitor's last arrival.
 * Null when the tracking bootstrap is missing or has recorded no arrival, or when the
 * last arrival carries no campaign parameter (nothing worth storing).
 */
export const getLastArrivalUtms = (): UtmParams | null => {
  try {
    const data: any = (window as any).eventsTracking?.getData?.();
    const arrivals: any[] = Array.isArray(data?.arrivals) ? data.arrivals : [];
    if (arrivals.length === 0) return null;
    const last = extractUtms(arrivals[arrivals.length - 1]?.url);
    return Object.keys(last).length === 0 ? null : last;
  } catch {
    return null;
  }
};
